
import Vue from 'vue';
import { Prop, Watch, Component } from 'vue-property-decorator';
import { postConsumerStatus, postEnrollmentResume } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication';
import { AxiosResponse, AxiosPromise } from 'axios';
import { PostConsumerStatusResponse } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication/types/ConsumerStatus';
import { PostEnrollmentResumeResponse } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication/types/Enrollment';
import { getModule } from 'vuex-module-decorators';
import LoginStore from '@/login/store';
import { store } from '@/store';

getModule(LoginStore, store);

@Component
export default class EnrollmentBase extends Vue {
	@Prop({ type: String, required: true }) instructions!: string;

	@Prop({ type: String, default: '', required: true }) name!: string;

	@Prop({ type: Boolean, default: false }) showExtraLink!: boolean;

	@Prop({ type: String, default: '' }) extraLinkText!: string;

	@Prop({ type: Boolean, default: false, required: true }) sessionExpired!: boolean;

	@Prop({ type: String, default: false, required: true }) timeoutState!: string;

	steps: Array<{ name: string; label: string}> = [
		{ name: 'createLogin', label: 'Create Login' },
		{ name: 'verifyMobile', label: 'Verify Mobile Device' },
		{ name: 'pinSetup', label: 'PIN Setup' },
	];

	activeStep: number = 2;

	token: string = '';

	last4Ssn: string = '';

	dateOfBirth: string = '';

	allowedMinutes: number = 10;

	TIMEOUT: string = 'enrollment-timeout';

	timeoutId?: number;

	get headline() {
		return this.$store.state.login.headline;
	}

	async created() {
		const sessionValidated: string | null = sessionStorage.getItem('enrollmentTouch');
		if (!sessionValidated) {
			try {
				this.token = sessionStorage.getItem('token') || '';
				this.last4Ssn = sessionStorage.getItem('last4Ssn') || '';
				this.dateOfBirth = sessionStorage.getItem('dateOfBirth') || 'false';
				const response: AxiosResponse<PostConsumerStatusResponse> = await (postConsumerStatus({
					token: this.token,
					ssn_last_4: this.last4Ssn,
					date_of_birth: this.dateOfBirth,
				}) as AxiosPromise<PostConsumerStatusResponse>);
				this.gotoCorrectEnrollmentStep(response);
			} catch (error) {
				this.expireSession();
			}
		}
	}

	mounted() {
		this.touch();
		window.addEventListener(this.TIMEOUT, this.expireSession.bind(this), false);
	}

	touch() {
		const allowedTime: number = this.allowedMinutes * 60 * 1000;
		const lastTouch: number = parseInt(window.sessionStorage.getItem('enrollmentTouch') as string, 10);
		const now: number = new Date().getTime();

		if (lastTouch && now - lastTouch > allowedTime) {
			this.expireSession();
			return;
		}

		window.clearTimeout(this.timeoutId);
		this.timeoutId = window.setTimeout(() => {
			this.expireSession();
		}, allowedTime);
		window.sessionStorage.setItem('enrollmentTouch', now.toString());
	}

	gotoCorrectEnrollmentStep(response: AxiosResponse<PostConsumerStatusResponse>) {
		const statusRoutes: { [key: string]: string } = {
			unregistered: 'createLogin',
			registered: 'verifyMobileDevice',
			confirmed: 'pinSetup',
			active: 'login',
		};
		const nextRoute: string = statusRoutes[response.data.data.status];
		const kjwt: string | null = sessionStorage.getItem('kjwt');

		switch (true) {
			case (nextRoute !== 'createLogin' && !kjwt):
				this.resumeEnrollment(nextRoute);
				break;
			case (this.$route.name === nextRoute):
				break;
			default:
				this.$router.push({ name: nextRoute });
				break;
		}
	}

	async resumeEnrollment(route: string) {
		try {
			const response: AxiosResponse<PostEnrollmentResumeResponse> = await (postEnrollmentResume({
				token: this.token,
				ssn_last_4: this.last4Ssn,
				date_of_birth: this.dateOfBirth,
			}) as AxiosPromise<PostEnrollmentResumeResponse>);
			this.setKjwt(response.data.data, route);
		} catch (error) {
			this.expireSession();
		}
	}

	setKjwt(kjwt: string, routeName: string) {
		sessionStorage.setItem('kjwt', kjwt);
		// Timesout in 10 minutes.
		const timeout: Date = new Date();
		timeout.setMinutes(timeout.getMinutes() + 10);
		sessionStorage.setItem('enrollmentTouch', timeout.valueOf().toString());
		this.$router.push({ name: routeName });
	}

	extraLinkClick() {
		const formattedName: string = this.name.charAt(0).toUpperCase() + this.name.slice(1);
		this.$root.$emit(`extraLink${formattedName}`);
	}

	expireSession() {
		sessionStorage.removeItem('enrollmentTouch');
		sessionStorage.removeItem('kjwt');
		this.$router.push({ name: 'enrollmentSessionExpiredMessage' });
	}

	@Watch('name', { immediate: true, deep: true })
	onNameChanged(val: string) {
		this.steps.find((value, index: number) => {
			if (value.name === val) {
				this.activeStep = index;
				return true;
			}
			return false;
		});
	}

	@Watch('sessionExpired', { immediate: true, deep: true })
	onSessionExpired(val: boolean) {
		if (val) {
			this.expireSession();
		}
	}

	@Watch('timeoutState', { immediate: true, deep: true })
	onTimeoutStateChange(val: string) {
		if (val === 'disabled') {
			window.clearTimeout(this.timeoutId);
			window.removeEventListener(this.TIMEOUT, this.expireSession.bind(this), false);
		} else if (val === 'reset') {
			this.touch();
		}
	}
}
